/**
 * The shell: skip link, masthead, navigation, footer.
 *
 * Two Design Bible rules are carried here rather than in each view. 9.2 puts a
 * `Skip to main content` link first in the tab order of every app. 9.1 (3.2.6
 * Consistent Help) puts the same help affordance in the same position on every
 * page — on the storefront that is the `Ask about an item` entry point, which
 * is why it is in the masthead and in the footer rather than only on an item
 * page.
 *
 * The wordmark is typographic. Design Bible 16 records the project name and
 * logo as operator-gate items that do not exist yet, so the masthead sets the
 * placeholder in the display face rather than reserving space for an asset
 * nobody has approved.
 */

import type { ReactNode } from "react";
import { useStorefront } from "../app/StorefrontContext.js";
import { InternalLink } from "./InternalLink.js";

export function Shell({ children }: { children: ReactNode }): ReactNode {
  const { config, cart, route } = useStorefront();
  const cartCount = cart.length;

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to main content
      </a>
      <header className="masthead">
        <InternalLink className="wordmark t-display-xs" href="/catalog">
          <span aria-hidden="true" className="wordmark-mark" />
          {config.project_name}
        </InternalLink>
        <nav aria-label="Catalogue" className="masthead-nav">
          <InternalLink
            className="masthead-link"
            current={route.name === "catalog"}
            href="/catalog"
          >
            Catalogue
          </InternalLink>
          <InternalLink className="masthead-link" current={route.name === "help"} href="/help">
            Ask about an item
          </InternalLink>
          {config.account_enabled ? (
            <InternalLink
              className="masthead-link"
              current={route.name === "account"}
              href="/account"
            >
              Your account
            </InternalLink>
          ) : null}
          <InternalLink className="masthead-link" current={route.name === "cart"} href="/cart">
            Cart{" "}
            <span className="t-id" data-testid="cart-count">
              ({cartCount})
            </span>
          </InternalLink>
        </nav>
      </header>

      <main className="main" id="main" tabIndex={-1}>
        {children}
      </main>

      <footer className="footer">
        <nav aria-label="Policies and help" className="footer-nav">
          <InternalLink className="masthead-link" href="/policies">
            Shipping, returns, and terms
          </InternalLink>
          <InternalLink className="masthead-link" href="/help">
            Ask about an item
          </InternalLink>
        </nav>
        <p className="t-body-s t-secondary">
          Every item in this catalogue is one of one. Photographs are of the exact object offered,
          not stock art.
        </p>
      </footer>
    </div>
  );
}
